/**
 * New-release detection (#4243).
 *
 * The stale-chunk recovery in chunkReload.ts only fires when a tab asks for a
 * chunk the new build no longer ships. A tab that has already loaded a page's
 * code keeps running it after a deploy -- so a fixed form can stay broken in
 * that tab for hours. This module compares the build id compiled into the
 * running bundle with the one the server publishes at /version.json, so the
 * app can offer a reload. It never reloads by itself: that would lose unsaved
 * form input.
 *
 * Every failure (no build id, missing file, SPA fallback HTML, network error)
 * reads as "no newer release", never as an error.
 */

/** Minimum gap between two /version.json requests. */
export const RELEASE_CHECK_THROTTLE_MS = 60_000;

/** Background re-check interval for a tab that is left open. */
export const RELEASE_CHECK_INTERVAL_MS = 10 * 60_000;

/** The build id compiled into this bundle; empty under dev serve and tests. */
export function runningBuildId(): string {
  return import.meta.env.VITE_APP_BUILD_ID ?? "";
}

/** Fetch the build id the server is publishing now, or null if unknown. */
export async function fetchDeployedBuildId(
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  try {
    const response = await fetchImpl(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      credentials: "omit",
    });
    if (!response.ok) return null;
    // A missing file can fall back to index.html; only trust real JSON.
    const type = response.headers.get("content-type") ?? "";
    if (!type.includes("json")) return null;
    const body: unknown = await response.json();
    const id = (body as { build_id?: unknown } | null)?.build_id;
    return typeof id === "string" && id ? id : null;
  } catch {
    return null;
  }
}

/** True only when both ids are known and they differ. */
export function isNewerRelease(running: string, deployed: string | null): boolean {
  return Boolean(running) && Boolean(deployed) && running !== deployed;
}

/**
 * A throttled checker. `check()` resolves to the newer deployed build id, or
 * null when there is none (or the call was throttled / the check is off).
 */
export function createReleaseChecker(options: {
  running: string;
  fetchImpl?: typeof fetch;
  now?: () => number;
  throttleMs?: number;
}) {
  const {
    running,
    fetchImpl,
    now = Date.now,
    throttleMs = RELEASE_CHECK_THROTTLE_MS,
  } = options;
  let lastCheck = Number.NEGATIVE_INFINITY;
  let inFlight = false;

  return {
    async check(): Promise<string | null> {
      if (!running || inFlight) return null;
      const at = now();
      if (at - lastCheck < throttleMs) return null;
      lastCheck = at;
      inFlight = true;
      try {
        const deployed = await fetchDeployedBuildId(fetchImpl);
        return isNewerRelease(running, deployed) ? deployed : null;
      } finally {
        inFlight = false;
      }
    },
  };
}
